import { useEffect, useState } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { RESTAURANT } from '../constants/restaurant';

const NAV_LINKS = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Menu', href: RESTAURANT.orderUrl, external: true },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Contact', href: '#contact' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const close = () => setOpen(false);

  // Text color tokens that adapt to scroll state
  const brandText = scrolled ? 'text-brand-coffee' : 'text-brand-cream';
  const brandSub = scrolled ? 'text-brand-mocha' : 'text-brand-cream/75';
  const linkText = scrolled
    ? 'text-brand-coffee/80 hover:text-brand-burgundy'
    : 'text-brand-cream/90 hover:text-brand-gold';
  const phoneText = scrolled
    ? 'text-brand-coffee hover:text-brand-burgundy'
    : 'text-brand-cream hover:text-brand-gold';
  const bookBtn = scrolled
    ? 'border-brand-coffee/25 text-brand-coffee hover:bg-brand-coffee hover:text-brand-cream'
    : 'border-brand-cream/40 text-brand-cream hover:bg-brand-cream hover:text-brand-coffee';
  const toggleBtn = scrolled
    ? 'bg-brand-cream/90 border-brand-ivory text-brand-coffee'
    : 'bg-brand-coffee/40 border-brand-cream/30 text-brand-cream backdrop-blur';

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-brand-cream/90 backdrop-blur-xl border-b border-brand-ivory shadow-[0_8px_30px_rgba(43,27,23,0.06)]'
          : 'bg-gradient-to-b from-black/35 to-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16 flex items-center justify-between h-[72px] md:h-[88px]">
        <a
          href="#home"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group shrink-0"
          onClick={close}
        >
          <span className="h-14 md:h-16 w-20 md:w-24 rounded-xl bg-white border border-white/40 shadow-md flex items-center justify-center overflow-hidden px-2 py-1.5">
            <img
              src={RESTAURANT.logoUrl}
              alt="Sankalp - The Taste of India"
              className="w-full h-full object-contain"
            />
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className={`font-serif text-lg md:text-xl transition-colors ${brandText}`}>
              Sankalp
            </span>
            <span
              className={`text-[9px] md:text-[10px] uppercase tracking-[0.22em] font-semibold transition-colors ${brandSub}`}
            >
              Sunshine&nbsp;Coast
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm font-medium uppercase tracking-[0.18em] transition-colors ${linkText}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${RESTAURANT.phoneRaw}`}
            data-testid="navbar-call-link"
            className={`hidden xl:inline-flex items-center gap-2 text-sm font-medium transition ${phoneText}`}
          >
            <Phone className="w-4 h-4" /> {RESTAURANT.phone}
          </a>
          <a
            href={RESTAURANT.bookUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-book-btn"
            className={`px-5 py-2.5 rounded-full border text-xs font-semibold tracking-[0.18em] uppercase transition-all duration-300 ${bookBtn}`}
          >
            Book a Table
          </a>
          <a
            href={RESTAURANT.orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-order-btn"
            className="px-5 py-2.5 rounded-full bg-brand-burgundy text-brand-cream text-xs font-semibold tracking-[0.18em] uppercase hover:bg-brand-coffee transition-all duration-300 shadow-[0_8px_24px_rgba(139,26,26,0.25)] hover:-translate-y-0.5"
          >
            Order Online
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className={`lg:hidden w-10 h-10 rounded-full border flex items-center justify-center transition ${toggleBtn}`}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-brand-cream/95 backdrop-blur-xl border-b border-brand-ivory ${
          open ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-4" data-testid="mobile-nav">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              onClick={close}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className="font-serif text-2xl text-brand-coffee hover:text-brand-burgundy transition"
            >
              {l.label}
            </a>
          ))}
          <a
            href={RESTAURANT.orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            data-testid="mobile-nav-order"
            className="font-serif text-2xl text-brand-burgundy"
          >
            Order Online
          </a>
          <a
            href={RESTAURANT.bookUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            data-testid="mobile-nav-book"
            className="font-serif text-2xl text-brand-burgundy"
          >
            Book a Table
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
